import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearTestResult } from '../../actions/testActions';

function ExamResult() {
  const finalresult = useSelector(state => state.tests.finalresult);
  const location = useLocation();
  const { test, userAnswers } = location?.state || {};
  const [showAnswers, setShowAnswers] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Prevent going back to test page
  useEffect(() => {
    const handleBackButton = (e) => {
      e.preventDefault();
      dispatch(clearTestResult());
      navigate('/');
    };

    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
      if (!window.location.pathname.includes('/exam-result')) {
        dispatch(clearTestResult());
      }
    };
  }, [dispatch, navigate]);

  const handleGoDashboard = () => {
    dispatch(clearTestResult());
    navigate('/');
  };

  if (!finalresult || (Array.isArray(finalresult) && finalresult.length === 0)) {
    return (
      <div className="container text-center py-5">
        <p className="text-center text-muted p-3">
          <strong>No Test Results Found.</strong><br />
          Don't worry! If you have already submitted your test successfully, your result will be available shortly on the dashboard.
        </p>

        <button
          className="btn mt-3"
          onClick={handleGoDashboard}
          style={{ backgroundColor: '#0096c7', color: 'white' }}
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  // Helper function to check if answer is correct for both single and multiple choice
 const isAnswerCorrect = (questionIndex) => {
    const userAnswer = userAnswers[questionIndex];
    const correctAnswer = finalresult.allcorrectAnswers[questionIndex];
    const questionType = test.questions[questionIndex].type;

    if (userAnswer === null || userAnswer === undefined) {
        return false;
    }

    if (questionType === 'multiple') {
        // Multiple choice question - order shouldn't matter
        if (!Array.isArray(userAnswer) || !Array.isArray(correctAnswer)) {
            return false;
        }
        
        // Convert to Sets for order-independent comparison
        const userSet = new Set(userAnswer);
        const correctSet = new Set(correctAnswer);
        
        // Check if sets are identical (same size and all elements match)
        return userSet.size === correctSet.size && 
               [...userSet].every(val => correctSet.has(val));
    } else {
        // Single choice question
        return userAnswer === correctAnswer;
    }
};

  const getAnswerStatus = (questionIndex, optionIndex) => {
    const userAnswer = userAnswers[questionIndex];
    const correctAnswer = finalresult.allcorrectAnswers[questionIndex];
    const questionType = test.questions[questionIndex].type;

    if (questionType === 'multiple') {
      // Multiple choice question logic
      const isCorrect = Array.isArray(correctAnswer) && correctAnswer.includes(optionIndex);
      const isUserSelected = Array.isArray(userAnswer) && userAnswer.includes(optionIndex);

      if (isCorrect && isUserSelected) return 'correct';
      if (isCorrect && !isUserSelected) return 'correct-not-selected';
      if (!isCorrect && isUserSelected) return 'incorrect';
        return '';
    } else {
      // Single choice question logic
      if (userAnswer === null) {
        return optionIndex === correctAnswer ? 'correct-not-selected' : '';
      }

      if (optionIndex === correctAnswer) return 'correct';
      if (optionIndex === userAnswer) return 'incorrect';
      return '';
    }
  };

  const getAnswerStatusText = (questionIndex) => {
    const userAnswer = userAnswers[questionIndex];

    if (userAnswer === null) {
      return <span className="badge bg-warning">Not Attempted</span>;
    }

    return isAnswerCorrect(questionIndex)
      ? <span className="badge bg-success">Correct</span>
      : <span className="badge bg-danger">Incorrect</span>;
  };


  // Function to get question type badge
  const getQuestionTypeBadge = (type) => {
    return type === 'multiple'
      ? <span className="badge bg-info ms-2">Multiple Choice</span>
      : <span className="badge bg-info ms-2">Single Choice</span>;
  };

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10 col-xl-8">
          <div className="card shadow">
            <div className="card-header text-white" style={{ backgroundColor: '#0096c7' }}>
              <h2 className="mb-0 fs-4">Test Results</h2>
            </div>
            <div className="card-body">
              <div className="result-summary">
                <div className={`status-badge ${finalresult.passStatus === 'Pass' ? 'bg-success' : 'bg-danger'} text-white p-2 rounded mb-4 text-center`}>
                  <h3 className="mb-0 fs-5">{finalresult.passStatus}</h3>
                </div>

                <div className="row g-3 text-center">
                  <div className="col-6 col-md-3">
                    <div className="p-3 border rounded h-100">
                      <h6 className="mb-2">Total Questions</h6>
                      <p className="h4 mb-0">{finalresult.totalQuestions}</p>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-3 border rounded h-100">
                      <h6 className="mb-2">Correct Answers</h6>
                      <p className="h4 mb-0 text-success">{finalresult.correctAnswers}</p>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-3 border rounded h-100">
                      <h6 className="mb-2">Total Marks</h6>
                      <p className="h4 mb-0">{finalresult.totalMarks}</p>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-3 border rounded h-100">
                      <h6 className="mb-2">Obtained Marks</h6>
                      <p className="h4 mb-0 text-primary">{finalresult.obtainedMarks}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Percentage Score</h6>
                    <span className="h6 mb-0">{finalresult.percentageObtained}%</span>
                  </div>
                  <div className="progress" style={{ height: '20px' }}>
                    <div
                      className={`progress-bar ${finalresult.passStatus === 'Pass' ? 'bg-success' : 'bg-danger'}`}
                      role="progressbar"
                      style={{ width: `${finalresult.percentageObtained}%` }}
                      aria-valuenow={finalresult.percentageObtained}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                </div>

                <div className="d-flex  justify-content-center mt-4">
                  <button
                    className="btn me-2 button-design"
                    onClick={handleGoDashboard}
                  >
                    Go to Dashboard
                  </button>
                  <button
                    className="btn"
                    onClick={() => setShowAnswers(!showAnswers)}
                    style={{
                      borderColor: '#0096c7',
                      backgroundColor: showAnswers ? '#0096c7' : 'transparent',
                      color: showAnswers ? 'white' : '#0096c7'
                    }}
                  >
                    {showAnswers ? 'Hide Answers' : 'Show Answers'}
                  </button>
                </div>
              </div>

              {/* Questions Review Section */}
              {showAnswers && (
                <div className="questions-review mt-5">
                  <h4 className="mb-4 fs-5">Questions Review</h4>
                  {test?.questions?.map((question, questionIndex) => (
                    <div key={questionIndex} className="card mb-4">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h5 className="card-title mb-1">
                              <span className="badge bg-secondary me-2">{questionIndex + 1}</span>
                              <span dangerouslySetInnerHTML={{ __html: question.questionText }} />
                            </h5>
                            {getQuestionTypeBadge(question.type)}
                          </div>
                          {getAnswerStatusText(questionIndex)}
                        </div>

                        <div className="options-list mt-3">
                          {question.options.map((option, optIndex) => {
                            const status = getAnswerStatus(questionIndex, optIndex);
                            return (
                              <div
                                key={optIndex}
                                className={`p-3 rounded mb-2 ${status === 'correct' ? 'bg-success bg-opacity-10 border border-success' :
                                    status === 'incorrect' ? 'bg-danger bg-opacity-10 border border-danger' :
                                      status === 'correct-not-selected' ? 'bg-success bg-opacity-10 border border-success border-dashed' :
                                        'bg-light'
                                  }`}
                              >
                                <div className="form-check d-flex align-items-center">
                                  <input
                                    className="form-check-input me-2"
                                    type={question.type === 'multiple' ? 'checkbox' : 'radio'}
                                    checked={
                                      question.type === 'multiple'
                                        ? Array.isArray(userAnswers[questionIndex]) && userAnswers[questionIndex].includes(optIndex)
                                        : userAnswers[questionIndex] === optIndex
                                    }
                                    readOnly
                                  />
                                  <label className="form-check-label d-flex align-items-center flex-grow-1">
                                    <span className="fw-bold me-2">{String.fromCharCode(65 + optIndex)}.</span>
                                    <span>{option}</span>
                                    {status === 'correct' && (
                                      <span className="ms-auto badge bg-success">
                                        <i className="fas fa-check me-1"></i>Correct
                                      </span>
                                    )}
                                    {status === 'incorrect' && (
                                      <span className="ms-auto badge bg-danger">
                                        <i className="fas fa-times me-1"></i>Your Answer
                                      </span>
                                    )}
                                    {status === 'correct-not-selected' && (
                                      <span className="ms-auto badge bg-success">
                                        <i className="fas fa-check me-1"></i>Correct Answer
                                      </span>
                                    )}
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {question.explanation && (
                          <div className="explanation mt-3 p-3 bg-light rounded">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-info-circle text-primary me-2"></i>
                              <strong>Explanation:</strong>
                            </div>
                            <div className="ps-3">{question.explanation}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        .border-dashed {
          border-style: dashed !important;
        }
        .explanation {
          border-left: 3px solid #0096c7;
        }
        .options-list .form-check-input {
          cursor: default;
        }
        @media (max-width: 768px) {
          .card-title {
            font-size: 1rem;
          }
          .options-list {
            font-size: 0.9rem;
          }
          .h4 {
            font-size: 1.2rem;
          }
          .fs-5 {
            font-size: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
}

export default ExamResult;